import { Component, Input, OnInit, EventEmitter, Output, OnDestroy, OnChanges } from '@angular/core';
import { UtilService } from '../../../../shared/util.service';
import { TrendsService } from '../../../followup/trends/trends.service';
import { Hotel } from 'app/shared/model';
import { DateService } from 'app/shared/services/date.service';
import { skip } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';

@Component({
    templateUrl: './trends-section.component.html',
    selector: 'mh-chain-followup-trends',
    standalone: false
})
export class TrendsSectionComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  productId = 1;

  @Input()
  showAreas = true;

  @Input()
  showFilters = true;

  @Input()
  set selectedArea(area: number | null) {
    if (area !== null && area !== undefined) {
      this._areaSelected = area;
    }
  }

  @Output() areaChanged = new EventEmitter<number>();
  @Output() isReady = new EventEmitter<boolean>();

  chainFilter: Hotel[] = [];
  originalChainFilter: Hotel[] = [];
  report = '';
  util: any;

  fidelityPermisions: string[] = [];
  prefixTranslateFilters = 'widgets.trends.filters';
  trendSelected: string;
  _areaSelected: number | null = null;
  currentTrend: any;
  data: any;
  segmentedBy: string;
  areas: any[] = [];
  subscription: Subscription;
  surveysReady = false;

  actions: { [key: string | number]: string | number } = {
    nps: 'getHistoricNPS',
    ranking_areas: 'getHistoricArea',
    ranking: 'getTripAdvisor',
    pms: 'getHistoricPMS',
    sent: 'getEmailsArea',
    answered: 'getEmailsArea',
    email_surveys_answered: 'getEmailsArea',
    whatsapp_surveys_answered: 'getEmailsArea',
    open_surveys_answered: 'getEmailsArea',
    rate: 'getEmailsArea',
    email_surveys: 'getEmailsArea',
    whatsapp_surveys: 'getEmailsArea',
    open_surveys: 'getEmailsArea',
    total_guests: 'getHistoricPMS',
    promoters: 'getHistoricNPS',
    passives: 'getHistoricNPS',
    detractors: 'getHistoricNPS',
    survey_total_score: 'getScore',
    semantic_index: 'getSemanticIndex',
    sentiment_index: 'getSentimentIndex',
  };
  optionsWithTag: string[] = ['sentiment_index'];

  constructor(
    private utilService: UtilService,
    private dateService: DateService,
    private trendsService: TrendsService,
    private route: ActivatedRoute,
  ) {
    this.util = this.utilService;
  }

  ngOnChanges(): void {
    this.report = this.productId === 1 ? 'chainFollowupTrends' : 'chainOnsiteFollowupTrends';
  }

  ngOnInit(): void {
    this.fidelityPermisions = this.utilService.getFidelityConfigurationsByProduct('interchain', 'interchain_followup');
    this.report = this.productId === 1 ? 'chainFollowupTrends' : 'chainOnsiteFollowupTrends';

    const trendFromRoute = this.route.snapshot.queryParamMap.get('trend');
    const trendValue = trendFromRoute && trendFromRoute in this.actions ? trendFromRoute : 'nps';
    this.currentTrend = {
      name: `${this.prefixTranslateFilters}.${trendValue}`,
      original: trendValue,
      value: trendValue,
    };

    this.subscription = this.utilService
      .isCurrentSurveyChanged()
      .pipe(skip(1))
      .subscribe(() => {
        if (this.currentDates) {
          this.areas = [];
          this._areaSelected = -1;
          this.loadData();
        }
      });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  setChain(chain: Hotel[]): void {
    setTimeout(() => {
      this.originalChainFilter = [...chain];
      this.chainFilter = [...chain];
    }, 0);
  }

  setHotelsFilter(hotels: Hotel[]): void {
    this.chainFilter = [...hotels];
  }

  dataIsReady(state: boolean): void {
    setTimeout(() => {
      this.isReady.emit(state);
    }, 200);
  }

  handleDatepickerUpdated($event: any): void {
    this.dateService.setTrendCurrentFilter($event.currentFilter);
    this.dateService.setTrendCurrentDates($event.currentDates);
    this.segmentedBy = this.dateService.getSegmentation();
  }

  setSelectlValue(option: any): void {
    this.trendSelected = option.value;

    if (option.value === 'ranking_areas') {
      this.currentTrend = option;
      this._areaSelected = -1;
      this.loadData();
    } else {
      this.currentTrend = option;
    }
  }

  setAreaValue(option: any, key: string): void {
    if (key === 'areas' && option) {
      this._areaSelected = +option.value;
      this.areaChanged.emit(this._areaSelected);
    }
  }

  loadData(): void {
    if (this.trendSelected !== 'ranking_areas') {
      return;
    }

    const language = this.utilService.getLanguageId();
    const survey = this.utilService.getCurrentSurvey(this.chainFilter.map((x) => parseInt(x.id, 10)));
    const surveyIds = survey?.ids || '';
    const version = survey?.survey?.[0]?.version;

    const chain = survey?.chain || this.chainFilter.map((x) => x.id).join(',');

    if (!this.segmentedBy) {
      this.segmentedBy = this.dateService.getSegmentation();
    }

    const customers = chain;

    if (customers.length) {
      this.trendsService
        .getHistoricArea(
          { startDate: this.startDate, endDate: this.endDate },
          customers,
          this.productId,
          language,
          this.segmentedBy,
          surveyIds,
          version,
        )
        .subscribe(
          (data: any) => {
            this.data = data;

            if (this.data && this.data.values) {
              let allAreas = [];

              this.data.values.forEach((value) => {
                if (value.periods) {
                  value.periods.forEach((period) => {
                    if (period.data?.ranking_areas?.areas) {
                      const periodAreas = period.data.ranking_areas.areas.map((area) => area.id_area);
                      allAreas = allAreas.concat(periodAreas);
                    }
                  });
                }
              });

              this.areas = this.utilService
                .uniqArray(allAreas)
                .sort((a, b) => a - b)
                .map((area) => {
                  return { [area]: area };
                });

              if (this.areas.length > 0 && !this._areaSelected) {
                this._areaSelected = -1;
                this.areaChanged.emit(this._areaSelected);
              }
            }

            this.isReady.emit(true);
          },
          (error) => {
            console.error(error);
          },
        );
    }
  }

  get currentDates() {
    return this.dateService.trendCurrentDates;
  }

  get startDate() {
    return this.currentDates.startDate;
  }

  get endDate() {
    return this.currentDates.endDate;
  }

  get currentFilterDate() {
    return this.dateService.trendCurrentFilter;
  }

  get reportSlug(): string {
    return this.productId === 1 ? 'corporative_followup_trends_report' : 'corporative_onsite_trends_report';
  }

  get areasList(): { [key: string]: number } {
    return this.areas.reduce(
      (result, areaObj) => {
        const key = Object.keys(areaObj)[0];
        result[key] = +key;
        return result;
      },
      { '-1': -1 },
    );
  }

  get areaSelected(): number | null {
    return this._areaSelected;
  }

  onSurveysLoaded(): void {
    this.surveysReady = true;
  }
}
